<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <div v-else>
    <TableRemote
      v-if="hasPermission('analyses.categories.view')"
      :iconHeader="hasPermission('analyses.categories.add') ? 'add' : null"
      :items="displayedCategories"
      isSearchable
      :headers="tableHeaders"
      :itemActions="actions"
      :isCustomIcon="true"
      :sortableColumns="sortableColumns"
      :title="$t('analyses.categories.pageName')"
      :pagination="pagination"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      hideDefaultFooter
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import NameForm from '@/components/forms/NameForm';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {
        NoPermissionAlert,
        TableRemote,
    },
    data() {
        return {
            categories: [],
            pagination: {},
            queryParams: {
                perpage,
                page: 1,
            },
            loading: false,
            fetching: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
            'teamLangs',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: true,
            }));
        },
        sortableColumns() {
            return this.tableHeaders
                .filter(column => column.sortable)
                .map(column => this.$options.filters.lowercase(column.value));
        },
        displayedCategories() {
            return this.categories.map(category => ({
                id: category.id,
                name: category.name[this.userLang] || category.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('analyses.categories.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('analyses.categories.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getCategories();
        this.loading = false;
    },
    methods: {
        async getCategories(nextPage = 1) {
            if (this.fetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getAnalysisCategories(this.queryParams);
                this.categories = [...data.categories];
                this.pagination = data.pagination;
                return;
            }
            if (nextPage <= this.queryParams.page) return;

            this.fetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getAnalysisCategories(this.queryParams);
            this.categories = [...this.categories, ...data.categories];
            this.pagination = data.pagination;
            this.fetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('analyses.categories.add'),
                component: Vue.component('NameForm', NameForm),
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addCategory(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getAnalysisCategory(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.categories.update'),
                    component: Vue.component('NameForm', NameForm),
                    showLangs: true,
                    isForm: true,
                    payload: { name: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateCategory(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.categories.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('analyses.categories.confirmRemove') },
                    onConfirm: () => this.removeCategory(item),
                    isForm: false,
                });
            }
        },
        async addCategory(requestPayload) {
            await this.$rq.addAnalysisCategory(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.categories.added', { name: requestPayload.name[this.userLang] }),
            });
            await this.getCategories();
        },
        async updateCategory(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateAnalysisCategory(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.categories.updated', { name: requestPayload.name[this.userLang] }),
            });
            await this.getCategories();
        },
        async removeCategory(category) {
            await this.$rq.removeAnalysisCategory(category.id);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.categories.removed', { name: category.name[this.userLang] }),
            });
            await this.getCategories();
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams };
            await this.getCategories();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.fetching) {
                await this.getCategories(this.queryParams.page + 1);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
